import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Loader2, AlertCircle, ZoomIn, ZoomOut } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { getSessionToken } from './announcementUtils';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * In-app PDF viewer for announcements. Zoom in/out only — no download, no print,
 * no browser PDF toolbar (rendered to canvas via react-pdf).
 */
export default function AnnouncementPdfViewer({ url }) {
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfData, setPdfData] = useState(null);
  const [scale, setScale] = useState(1.3);
  // Render the canvas backing store at the screen's real pixel density (capped at 3x)
  // so text/graphics stay crisp on high-DPI displays and at every zoom level.
  const dpr = Math.min(Math.max(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2), 3);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(''); setPdfData(null);
      try {
        const sep = url.includes('?') ? '&' : '?';
        const res = await fetch(`${BACKEND_URL}${url}${sep}token=${encodeURIComponent(getSessionToken())}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        setPdfData({ data: buf });
      } catch {
        if (!cancelled) setError('Failed to load document');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  const onLoadSuccess = useCallback(({ numPages }) => setNumPages(numPages), []);
  const zoomIn = () => setScale(s => Math.min(s + 0.2, 3));
  const zoomOut = () => setScale(s => Math.max(s - 0.2, 0.6));

  return (
    <div className="announcement-pdf-viewer border rounded-lg overflow-hidden bg-muted/30" data-testid="announcement-pdf-viewer">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <span className="text-xs text-muted-foreground">{numPages ? `${numPages} page${numPages > 1 ? 's' : ''}` : 'Document'}</span>
        <div className="flex items-center gap-1 border rounded-md">
          <Button variant="ghost" size="icon" onClick={zoomOut} className="h-8 w-8" data-testid="pdf-zoom-out"><ZoomOut className="w-4 h-4" /></Button>
          <span className="text-xs w-12 text-center">{Math.round(scale * 100)}%</span>
          <Button variant="ghost" size="icon" onClick={zoomIn} className="h-8 w-8" data-testid="pdf-zoom-in"><ZoomIn className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="overflow-auto" style={{ height: '68vh' }} onContextMenu={(e) => e.preventDefault()}>
        {loading && (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>
        )}
        {error && !loading && (
          <div className="flex flex-col items-center justify-center gap-3 py-24"><AlertCircle className="w-10 h-10 text-destructive" /><p className="text-sm text-muted-foreground">{error}</p></div>
        )}
        {pdfData && !loading && !error && (
          <Document file={pdfData} onLoadSuccess={onLoadSuccess} onLoadError={() => setError('Failed to render PDF')}
            loading={<div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}>
            {numPages && Array.from({ length: numPages }, (_, i) => (
              <div key={i} className="flex justify-center py-2">
                <Page pageNumber={i + 1} scale={scale} devicePixelRatio={dpr} renderTextLayer renderAnnotationLayer loading="" />
              </div>
            ))}
          </Document>
        )}
      </div>
    </div>
  );
}
